import loadable from '@loadable/component';
import { useTrans } from 'core/hooks';
import actions from 'core/redux/features/filters/actions';
import {
  ActiveFiltersType,
  ITimeRangeFilter,
} from 'core/redux/features/filters/types';
import { TransFn, selectTranslator } from 'core/redux/features/languages/trans';
import { selectActiveProductType } from 'core/redux/features/products/reducers/productTypes';
import { AppState } from 'core/redux/reducers';
import { debounce } from 'core/utils';
import { TimeUtil } from 'core/utils/date';
import { _get } from 'core/utils/object-prop';
import React, { Component, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import FilterItem from './FilterItem';

const Slider = loadable(() => import('Components/Elements/Slider'));

interface ITimeFilterProps {
  format?: string;
}

export const TimeFilter = ({ format = 'HH.MM' }: ITimeFilterProps) => {
  const trans = useTrans();
  const dispatch = useDispatch();
  const { time_list } = useSelector((state: AppState) => state.filters.times);
  const [sliderValues, setSliderValue] = useState<number[]>([
    0,
    time_list.length - 1,
  ]);

  const productType = useSelector(selectActiveProductType);
  trans('arrival_time');
  return (
    <FilterItem
      isOpen={false}
      hideArrowIcon={true}
      name="filter-time"
      label={trans(`arrival_time_${productType['id']}`)}
    >
      <Slider
        values={sliderValues}
        domain={[0, time_list.length - 1]}
        getDisplayValue={(value: number) => {
          const find = time_list[value];
          if (!find) {
            return '';
          }

          return new TimeUtil(find).format(format);
        }}
        onUpdate={(update: ReadonlyArray<number>) => {
          setSliderValue([...update]);
          const startTime = time_list[update[0]];
          const endTime = time_list[update[1]];
          if (startTime && endTime) {
            dispatch(actions.selectTime([startTime, endTime]));
          }
        }}
      />
    </FilterItem>
  );
};

interface IProps {
  trans: TransFn;
  format: string;
  deviceType: string;
  activeFilters: ActiveFiltersType;
  times: ITimeRangeFilter;
  onSelectTime: (update: string[]) => void;
  onUpdate?: (startTime, endTime) => void;
}

interface IState {
  startTime: string;
  endTime: string;
}

class FilterTime extends Component<IProps & RouteComponentProps<{}>, IState> {
  /**
   * This method will trigger every slide tick,
   * We need debounce to improve performance
   */
  private onSelectTime = debounce((items: string[]) => {
    const startTime = new TimeUtil(items[0]);
    const endTime = new TimeUtil(items[1]);

    this.props.onSelectTime(items);
    if (this.props.onUpdate) {
      this.props.onUpdate(startTime.toString(), endTime.toString());
    }
  });

  constructor(props: IProps & RouteComponentProps<{}>) {
    super(props);

    const {
      props: {
        activeFilters: { times },
      },
    } = this;

    // we will use sate for fast rendering of times in UI
    this.state = {
      startTime: times[0],
      endTime: times[1],
    };
  }

  onUpdate = (update: ReadonlyArray<number>) => {
    const startTime = this.props.times.time_list[update[0]];
    const endTime = this.props.times.time_list[update[1]];

    this.setState({
      startTime,
      endTime,
    });

    this.onSelectTime([startTime, endTime]);
  };

  render() {
    const {
      props: {
        times: { time_list },
        trans,
      },
    } = this;
    const selected = [this.state.startTime, this.state.endTime];
    const timeListSize = time_list.length - 1;
    let sliderValues = [0, timeListSize];

    if (selected) {
      sliderValues = [
        time_list.findIndex(time => selected[0] === time),
        time_list.findIndex(time => selected[1] === time),
      ];
    }

    return (
      <FilterItem
        isOpen={false}
        hideArrowIcon={true}
        name="filter-time"
        label={trans('arrival_time')}
      >
        <Slider
          values={sliderValues}
          domain={[0, timeListSize]}
          getDisplayValue={this.getTooltipValue}
          onUpdate={this.onUpdate}
        />
      </FilterItem>
    );
  }

  private getTooltipValue = (value: number, _percent: number) => {
    const find = _get<string>(this.props.times.time_list, value);

    return new TimeUtil(find).format(this.props.format);
  };
}

const mapStateToProps = (state: AppState, _props) => {
  // const {location} = props;

  return {
    times: state.filters.times,
    trans: selectTranslator(state),
    // activeFilters: selectActiveFilter(state.filters),
    deviceType: state.deviceType,
    // activeFiltersUrl: selectActiveInUrlSearch(state.filters, location.search)
  };
};

const mapDispatchToProps = {
  onSelectTime: actions.selectTime,
};

export const configure = connect(
  mapStateToProps,
  mapDispatchToProps,
)(FilterTime);

export default withRouter(configure);
